var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ElementStyles, FASTElement, attr, css } from "@microsoft/fast-element";
import { spacingHorizontalXL } from "@fluentui/web-components";
import { SvgService } from "./services/svg-service.js";
/**
 * SvgIcon
 * @summary This component is used to dynamically load and display SVG icons.
 *
 * @example
 * ```html
 * <fabric-svg-icon
 *   name="example-icon"
 *   size="24"
 *   path="/icons/example-icon.svg"
 *   aria-hidden="true"
 *   focusable="false"
 *   role="img"
 * ></fabric-svg-icon>
 * ```
 *
 * @attr {string} name - The name of the SVG icon.
 * @attr {string} size - The size of the SVG icon.
 * @attr {string} path - The path to the SVG icon file.
 * @attr {string} aria-hidden - The aria-hidden attribute for accessibility.
 * @attr {string} focusable - Indicates whether the SVG icon is focusable.
 *
 * @prop {ElementStyles | undefined} computedStylesheet - The computed stylesheet for the SVG icon.
 * @prop {boolean} isLoading - Indicates whether the icon is currently loading.
 * @prop {boolean} isError - Indicates whether there was an error loading the icon.
 * @prop {string} name - The name of the SVG icon.
 * @prop {string} size - The size of the SVG icon.
 * @prop {string} path - The path to the SVG icon file.
 * @prop {string} ariaHidden - The aria-hidden attribute for accessibility.
 * @prop {string} focusable - Indicates whether the SVG icon is focusable.
 * @prop {string | null} role - The role attribute for accessibility (native property, reflects to/from the role attribute).
 *
 * @csspart svg - The SVG element representing the icon.
 * @cssprop --icon-height - Controls the default height of the SVG icon.
 * @cssprop --icon-width - Controls the default width of the SVG icon.
 *
 * @method nameChanged - Handles changes to the name attribute.
 * @method sizeChanged - Handles changes to the size attribute.
 * @method pathChanged - Handles changes to the path attribute.
 * @method checkAndResolveRenderPromise - Checks and resolves the render promise if conditions are met.
 * @method renderIcon - Renders the SVG icon.
 * @method renderErrorIcon - Renders an error icon when the SVG fails to load.
 * @method updateComputedStylesheet - Updates the computed stylesheet with the provided width and height.
 * @method updateSvgAttributes - Updates the attributes of the SVG element.
 *
 * @fires {CustomEvent} error - Dispatched when the icon fails to load.
 *
 * @extends FASTElement
 * @tagname fabric-svg-icon
 * @public
 */
export class SvgIcon extends FASTElement {
    constructor() {
        super(...arguments);
        /**
         * Indicates whether the icon is currently loading.
         * @private
         * @type {boolean}
         * @default true
         */
        this.isLoading = true;
        /**
         * Indicates whether there was an error loading the icon.
         * @private
         * @type {boolean}
         * @default false
         */
        this.isError = false;
        /**
         * The aria-hidden attribute for accessibility.
         * @public
         * @attr
         * @type {string}
         * @default true
         */
        this.ariaHidden = "true";
        /**
         * Indicates whether the SVG icon is focusable.
         * @public
         * @attr
         * @type {string}
         * @default false
         */
        this.focusable = "false";
        /**
         * Resolver function for the render promise.
         * @private
         * @type {() => undefined | null}
         */
        this.renderResolver = () => undefined;
        /**
         * Promise that resolves when the icon is rendered.
         * @private
         * @type {Promise<void>}
         */
        this.renderPromise = new Promise((resolve) => {
            this.renderResolver = resolve;
        });
    }
    /**
     * Handles changes to the name attribute.
     * @public
     */
    nameChanged() {
        this.checkAndResolveRenderPromise();
    }
    /**
     * Handles changes to the size attribute.
     * @public
     */
    sizeChanged() {
        this.checkAndResolveRenderPromise();
    }
    /**
     * Handles changes to the path attribute.
     * @public
     */
    pathChanged() {
        this.checkAndResolveRenderPromise();
    }
    /**
     * The role attribute for accessibility.
     * @public
     * @type {string | null}
     * @default null
     */
    get role() {
        return this.getAttribute("role");
    }
    set role(value) {
        if (value === null || value === undefined) {
            this.removeAttribute("role");
        }
        else {
            this.setAttribute("role", value);
        }
    }
    /**
     * Called when the element is connected to the DOM.
     * @public
     */
    connectedCallback() {
        super.connectedCallback();
        this.renderPromise.then(() => this.renderIcon());
    }
    /**
     * Checks and resolves the render promise if conditions are met.
     * @private
     */
    checkAndResolveRenderPromise() {
        if (this.renderResolver) {
            if (this.path) {
                if (!this.name || (this.name && this.name !== "")) {
                    this.renderResolver();
                    this.renderResolver = () => undefined;
                }
            }
        }
    }
    /**
     * Renders the SVG icon.
     * @private
     */
    renderIcon() {
        this.isLoading = true;
        SvgService.getPreparedSVG(this.path, this.name)
            .then(({ content, width, height }) => {
            this.isLoading = false;
            this.shadowRoot.innerHTML = content;
            this.updateComputedStylesheet(width, height);
            const svgElementInShadow = this.shadowRoot.querySelector("svg");
            if (svgElementInShadow) {
                this.updateSvgAttributes(svgElementInShadow);
            }
        })
            .catch((error) => {
            this.isLoading = false;
            this.isError = true;
            console.error(`Failed to load icon: ${this.path}`, error);
            this.renderErrorIcon();
        });
    }
    /**
     * Renders an error icon when the SVG fails to load.
     * @private
     */
    renderErrorIcon() {
        // Creating SVG element for red "X"
        const errorSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        errorSvg.style.width = `var(--icon-width, ${spacingHorizontalXL})`;
        errorSvg.style.height = `var(--icon-height, ${spacingHorizontalXL})`;
        errorSvg.setAttribute("viewBox", "0 0 20 20");
        errorSvg.setAttribute("aria-hidden", "false");
        errorSvg.setAttribute("alt", "broken image");
        // Line from top-left to bottom-right
        const line1 = document.createElementNS("http://www.w3.org/2000/svg", "line");
        line1.setAttribute("x1", "0");
        line1.setAttribute("y1", "0");
        line1.setAttribute("x2", "20");
        line1.setAttribute("y2", "20");
        line1.setAttribute("stroke", "var(--colorPaletteRedForeground1)");
        line1.setAttribute("stroke-width", "var(--strokeWidthThick)");
        // Line from top-right to bottom-left
        const line2 = document.createElementNS("http://www.w3.org/2000/svg", "line");
        line2.setAttribute("x1", "20");
        line2.setAttribute("y1", "0");
        line2.setAttribute("x2", "0");
        line2.setAttribute("y2", "20");
        line2.setAttribute("stroke", "var(--colorPaletteRedForeground1)");
        line2.setAttribute("stroke-width", "var(--strokeWidthThick)");
        errorSvg.appendChild(line1);
        errorSvg.appendChild(line2);
        // Adding SVG to shadowRoot
        this.shadowRoot.innerHTML = "";
        this.shadowRoot.appendChild(errorSvg);
    }
    /**
     * Updates the computed stylesheet with the provided width and height.
     * @private
     * @param {string | undefined} width - The width of the SVG icon.
     * @param {string | undefined} height - The height of the SVG icon.
     */
    updateComputedStylesheet(width, height) {
        if (this.size) {
            if (["12", "16", "20", "24", "28", "32", "48"].includes(this.size)) {
                this.computedStylesheet = css `
          :host {
            --icon-height: ${this.size}px;
            --icon-width: ${this.size}px;
          }
        `;
            }
        }
        else if (width && height) {
            this.computedStylesheet = css `
        :host {
          --icon-height: ${height}px;
          --icon-width: ${width}px;
        }
      `;
        }
        if (this.computedStylesheet) {
            this.$fastController.addStyles(this.computedStylesheet);
        }
    }
    /**
     * Updates the attributes of the SVG element.
     * @private
     * @param {SVGSVGElement} svgElement - The SVG element to update.
     */
    updateSvgAttributes(svgElement) {
        var _a, _b;
        if (this.name !== undefined) {
            svgElement.setAttribute("name", this.name);
            // Extract size from icon name if not explicitly provided (e.g. "accessibility_20_filled" -> "20")
            if (!this.size && this.name.includes("_")) {
                const parts = this.name.split("_");
                const sizeFromName = parts.find((part) => /^\d+$/.test(part));
                if (sizeFromName) {
                    this.size = sizeFromName;
                }
            }
        }
        else {
            svgElement.removeAttribute("name");
        }
        // Handle size attribute
        let finalSize;
        if (this.size) {
            // First try to use the explicit size
            const numericSize = (_a = this.size.split("_")[0].match(/\d+/)) === null || _a === void 0 ? void 0 : _a[0];
            if (numericSize &&
                ["12", "16", "20", "24", "28", "32", "48"].includes(numericSize)) {
                finalSize = numericSize;
            }
            else {
                console.warn(`Invalid size "${this.size}" for SVG icon "${this.name}". Falling back to the default size token.`);
            }
        }
        if (!finalSize) {
            const cssSize = getComputedStyle(this)
                .getPropertyValue("--icon-width")
                .trim();
            const cssNumeric = (_b = cssSize.match(/\d+(?:\.\d+)?/)) === null || _b === void 0 ? void 0 : _b[0];
            if (cssNumeric) {
                finalSize = cssNumeric;
            }
        }
        if (finalSize) {
            svgElement.setAttribute("width", finalSize);
            svgElement.setAttribute("height", finalSize);
        }
        if (this.ariaLabel) {
            svgElement.setAttribute("aria-label", this.ariaLabel);
        }
        else {
            svgElement.removeAttribute("aria-label");
        }
        svgElement.setAttribute("aria-hidden", this.ariaHidden);
        svgElement.setAttribute("focusable", this.focusable || "false");
        if (this.role && this.role.trim() !== "") {
            svgElement.setAttribute("role", this.role);
        }
        else {
            svgElement.removeAttribute("role");
        }
    }
}
__decorate([
    attr
], SvgIcon.prototype, "name", void 0);
__decorate([
    attr
], SvgIcon.prototype, "size", void 0);
__decorate([
    attr
], SvgIcon.prototype, "path", void 0);
__decorate([
    attr({ attribute: "aria-hidden" })
], SvgIcon.prototype, "ariaHidden", void 0);
__decorate([
    attr
], SvgIcon.prototype, "focusable", void 0);
//# sourceMappingURL=svg-icon.js.map